import React, { useState, useEffect } from 'react';

const RobustImage = ({ 
  src, 
  category = 'products', 
  itemId = 'default',
  alt = 'Immagine',
  className = '',
  fallbackText = null,
  onLoad = null,
  onError = null,
  ...props 
}) => {
  const [currentSrc, setCurrentSrc] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [cacheKey] = useState(() => `img_${Date.now()}_${Math.random()}`); // Unique key per component instance

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Generate fallback URLs in order of preference
  const generateFallbackUrls = () => {
    const urls = [];

    console.log('🖼️ RobustImage received src:', src);
    console.log('📁 Category:', category, 'ItemId:', itemId);

    // 1. Original src if provided
    if (src) {
      // Check if it's a COMPLETE and VALID URL with /api/upload/serve/ or starts with http/https
      if (src.startsWith('http://') || src.startsWith('https://')) {
        // Check if it's missing /api/upload/serve/ and starts with admin/ or customer/
        if (src.includes('/admin/') || src.includes('/customer/')) {
          // Check if /api/upload/serve/ is missing
          if (!src.includes('/api/upload/serve/')) {
            // Extract the path part after the domain
            const urlObj = new URL(src);
            const pathPart = urlObj.pathname.replace(/^\//, ''); // Remove leading slash
            const fixedUrl = `${backendUrl}/api/upload/serve/${pathPart}`;
            console.log('🔧 Fixed URL (added /api/upload/serve/):', fixedUrl);
            urls.push(fixedUrl);
          } else {
            console.log('✅ Full HTTP URL with correct path, using as-is');
            urls.push(src);
          }
        } else {
          console.log('✅ Full HTTP URL (external or correct), using as-is');
          urls.push(src);
        }
      } 
      // Check if it starts with domain (without protocol)
      else if (src.includes('emergentagent.com') || src.includes('localhost')) {
        console.log('⚠️ URL without protocol detected, fixing...');
        // Check if /api/upload/serve/ is present
        if (!src.includes('/api/upload/serve/') && (src.includes('/admin/') || src.includes('/customer/'))) {
          const pathPart = src.split('/').slice(1).join('/'); // Get path after domain
          const fixedUrl = `${backendUrl}/api/upload/serve/${pathPart}`;
          console.log('🔧 Fixed URL:', fixedUrl);
          urls.push(fixedUrl);
        } else {
          urls.push(`https://${src}`);
        }
      }
      else {
        // Local upload path
        const cleanPath = src.replace(/^\/uploads\//, '').replace(/^uploads\//, '');
        const fullUrl = `${backendUrl}/api/upload/serve/${cleanPath}`;
        console.log('📂 Local path detected, constructing URL:', fullUrl);
        urls.push(fullUrl);
      }
    }

    // 2. Robust image with specific item ID
    if (itemId && itemId !== 'default') {
      urls.push(`${backendUrl}/api/upload/robust/${category}/${itemId}`);
    }

    // 3. Robust image with category default
    urls.push(`${backendUrl}/api/upload/robust/${category}/default`);

    // 4. Generic robust image
    urls.push(`${backendUrl}/api/upload/robust/products/default`);

    console.log('🔗 Fallback URLs generated:', urls);
    return urls;
  };

  // Preload image for better cache utilization
  const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = reject;
      img.src = url;
    });
  };

  // Try loading image with fallbacks
  const tryLoadImage = async (urls, index = 0) => {
    if (index >= urls.length) {
      // All URLs failed
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const url = urls[index];
    
    try {
      // Create a test image to check if URL loads
      const testImg = new Image();
      
      testImg.onload = () => {
        setCurrentSrc(url);
        setIsLoading(false);
        setHasError(false);
        if (onLoad) onLoad();
      };
      
      testImg.onerror = () => {
        console.warn(`Image failed to load: ${url}`);
        // Try next URL
        tryLoadImage(urls, index + 1);
      };
      
      testImg.src = url;
      
    } catch (error) {
      console.error(`Error loading image: ${url}`, error);
      tryLoadImage(urls, index + 1);
    }
  };

  // Retry mechanism
  const handleRetry = () => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      setIsLoading(true);
      setHasError(false);
      
      const urls = generateFallbackUrls();
      tryLoadImage(urls);
    }
  };

  // Initialize image loading
  useEffect(() => {
    const urls = generateFallbackUrls();
    if (urls.length > 0) {
      tryLoadImage(urls);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, [src, category, itemId, retryCount]);

  // Loading state
  if (isLoading) {
    return (
      <div 
        className={`bg-gray-200 animate-pulse flex items-center justify-center ${className}`}
        {...props}
      >
        <div className="text-gray-400 text-sm">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
    );
  }

  // Error state with retry option
  if (hasError) {
    return (
      <div 
        className={`bg-gray-100 flex flex-col items-center justify-center ${className}`}
        {...props}
      >
        <div className="text-gray-400 text-center p-4">
          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-xs text-gray-500 mb-2">
            {fallbackText || 'Immagine non disponibile'}
          </p>
          {retryCount < 3 && (
            <button 
              onClick={handleRetry}
              className="text-xs text-blue-500 hover:text-blue-700 underline"
            >
              Riprova
            </button>
          )}
        </div>
      </div>
    );
  }

  // Success state
  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={() => {
        console.error('Final image load failed:', currentSrc);
        setHasError(true);
        if (onError) onError();
      }}
      {...props}
    />
  );
};

export default RobustImage;